/**
 * Контекст для управління мовою інтерфейсу
 * Забезпечує перемикання між українською та англійською мовами
 * @context LanguageContext
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import enTranslations from '../locales/en.json';
import uaTranslations from '../locales/ua.json';

// Типи для мов
export type Language = 'en' | 'ua';

// Інтерфейс контексту мови
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, namespace?: string) => string;
}

// Об'єкт з перекладами
const translations = {
  en: enTranslations,
  ua: uaTranslations
};

// Створення контексту
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Провайдер контексту мови
interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('ua');
  const [currentTranslations, setCurrentTranslations] = useState(translations.ua);

  /**
   * Встановлює мову та оновлює переклади
   * @param lang - нова мова
   */
  const setLanguage = (lang: Language): void => {
    setLanguageState(lang);
    setCurrentTranslations(translations[lang]);
    localStorage.setItem('preferred-language', lang);
    console.log(`🌐 Мова змінена на: ${lang}`);
  };

  /**
   * Функція для отримання перекладу за ключем
   * @param key - ключ перекладу (може містити крапки для вкладених об'єктів)
   * @param namespace - простір імен (за замовчуванням 'common')
   * @returns перекладений текст або оригінальний ключ, якщо переклад не знайдено
   */
  const t = (key: string, namespace: string = 'common'): string => {
    const namespaceTranslations = (currentTranslations as any)[namespace];
    if (!namespaceTranslations) return key;

    // Підтримка вкладених ключів через крапку
    const keys = key.split('.');
    let value = namespaceTranslations;
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) return key;
    }

    return typeof value === 'string' ? value : key;
  };

  // Ефект для ініціалізації мови при завантаженні
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferred-language') as Language;
    const initialLanguage = savedLanguage || 'ua';
    
    setLanguageState(initialLanguage);
    setCurrentTranslations(translations[initialLanguage]);
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Хук для використання контексту мови
 * @returns об'єкт з функціоналом мови
 */
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
